import {
  ColumnDef,
  getCoreRowModel,
  PaginationState,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { HiSearch } from 'react-icons/hi';
import { skipToken } from '@reduxjs/toolkit/query';
import DataTable from '../../../../components/tables/dataTable';
import {
  enhancedCheckpointApi,
  useCheckpointServiceListCheckpointsForBuildingQuery
} from '../../../../api/enhanced/enhancedCheckpoints';
import { V1Alpha1Checkpoint } from '../../../../api/gen/checkpoints';
import CarbonDioxidePill from '../../../../components/pill/carbonDioxidePill';
import RowCell from '../../../../components/tables/rowCell';
import OnlineStatePill from '../../../../components/pill/onlineStatePill';
import DateTableLabel from '../../../../components/date/dateTableLabel';
import TableActionButton from '../../../../components/tables/tableActionButton';
import { useBuildingServiceListAreasForBuildingQuery } from '../../../../api/enhanced/enhancedBuildings';

interface CarbonDioxideOverviewTableProps {
  groupId?: string;
  buildingId?: string;
  areaId?: string;
  searchTerm?: string;
  clickHandler: (checkpointId: string) => void;
}

function CarbonDioxideOverviewTable({
  groupId,
  buildingId,
  areaId,
  searchTerm,
  clickHandler
}: CarbonDioxideOverviewTableProps): ReactElement {
  const [sortColumns, setSortColumns] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20
  });

  useEffect(() => {
    let sortingColumns = 'checkpoint_number:ASC';
    if (sorting.length > 0) {
      sorting.forEach((sortCol) => {
        sortingColumns = `${sortCol.id}:${sortCol.desc ? 'DESC' : 'ASC'}`;
      });
    }
    setSortColumns(sortingColumns);
  }, [sorting, setSortColumns]);

  useEffect(() => {
    enhancedCheckpointApi.util.invalidateTags(['SENSOR_TYPE_CARBON_DIOXIDE']);
    setPagination((state) => ({ ...state, pageIndex: 0 }));
    setSortColumns('checkpoint_number:ASC');
  }, [areaId, pageSize, searchTerm]);

  const { data: areaData, isError: areaIsError } =
    useBuildingServiceListAreasForBuildingQuery(
      buildingId != null && groupId != null
        ? { buildingId, groupId, limit: 200, page: 0 }
        : skipToken
    );

  const columns = useMemo<ColumnDef<V1Alpha1Checkpoint>[]>(() => {
    const getAreaDescriptionById = (id: string): string => {
      if (areaIsError) {
        return 'Fehler beim Laden der Bereiche';
      }

      const area = areaData?.areas?.find(
        (currentArea) => currentArea.id === id
      );
      return area != null && area.description != null
        ? area.description
        : 'Keinem Bereich zugewiesen';
    };

    return [
      {
        header: 'Prüfpunkt',
        id: 'checkpoint_number',
        cell: (info) => RowCell({ title: info.getValue<string>() }),
        accessorFn: (row) => row.checkpointNumber,
        footer: (props) => props.column.id
      },
      {
        header: 'Beschreibung',
        id: 'description',
        accessorFn: (row) => row.description,
        cell: (info) => RowCell({ title: info.getValue<string>() }),
        footer: (props) => props.column.id
      },
      {
        header: 'Bereich',
        accessorFn: (row) => row.areaId,
        cell: (info) =>
          RowCell({
            title: getAreaDescriptionById(info.getValue<string>())
          }),
        footer: (props) => props.column.id,
        enableSorting: false
      },
      {
        header: 'Kohlenstoffdioxid',
        id: 'carbon_dioxide',
        accessorFn: (row) => row.carbonDioxidePayload?.carbonDioxide,
        cell: (info) =>
          CarbonDioxidePill({
            value: info.getValue<number>(),
            alarmLimit:
              info.row.original.carbonDioxidePayload?.alarmLimit != null
                ? info.row.original.carbonDioxidePayload?.alarmLimit
                : 0,
            warningLimit:
              info.row.original.carbonDioxidePayload?.warningLimit != null
                ? info.row.original.carbonDioxidePayload?.warningLimit
                : 0
          }),
        footer: (props) => props.column.id
      },
      {
        header: 'Verfügbarkeit',
        id: 'is_online',
        accessorFn: (row) => row.loraMetadata?.isOnline,
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (info) => (
          <OnlineStatePill
            key={info.row.original.id}
            state={info.getValue<boolean>()}
            timestamp={info.row.original.loraMetadata?.lastSignal}
            checkpointId={info.row.original.id}
          />
        ),
        footer: (props) => props.column.id
      },
      {
        header: 'Letztes Signal',
        id: 'last_signal',
        cell: (info) =>
          DateTableLabel({
            timestamp: info.getValue<string>(),
            className: 'px-6'
          }),
        accessorFn: (row) => row.loraMetadata?.lastSignal,
        footer: (props) => props.column.id
      },
      {
        header: 'Details',
        accessorFn: (row) => row.id,
        cell: (info) =>
          TableActionButton({
            icon: <HiSearch size={20} />,
            onClick: () => clickHandler(info.getValue<string>())
          }),
        footer: (props) => props.column.id,
        enableSorting: false
      }
    ];
  }, [areaIsError, areaData?.areas, clickHandler]);

  const { data, isLoading, isSuccess, isError } =
    useCheckpointServiceListCheckpointsForBuildingQuery(
      buildingId != null && groupId != null && groupId !== ''
        ? {
            groupId,
            buildingId,
            areaId,
            sensorType: 'SENSOR_TYPE_CARBON_DIOXIDE',
            limit: pageSize,
            page: pageIndex,
            searchBy: searchTerm != null ? searchTerm : '',
            sortBy: sortColumns
          }
        : skipToken
    );

  const pagination = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageSize, pageIndex]
  );

  const total = Number(data?.total);
  const limit = data?.limit;
  const pageCount =
    total != null && limit != null ? Math.ceil(total / limit) : -1;

  const table = useReactTable({
    data: data?.checkpoints ?? [],
    columns,
    pageCount,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    state: { pagination, sorting },
    onSortingChange: setSorting,
    manualPagination: true,
    manualSorting: true
  });

  return (
    <DataTable
      isLoading={isLoading}
      isError={isError}
      isEmpty={
        isSuccess && (data.checkpoints == null || data.checkpoints.length === 0)
      }
      pageSize={pageSize}
      table={table}
    />
  );
}

export default CarbonDioxideOverviewTable;
